/**
 * Bootstrap initialization states that represent different phases of the shell-commons initialization process
 * These states follow a strict sequential order to ensure proper service initialization and dependency management
 */
export enum BootstrapState {
  /** Initial state - no initialization started */
  IDLE = 'idle',

  /** Configuration Discovery & Loading phase - Manifest processing and environment detection */
  CONFIGURATION_LOADING = 'configuration_loading',

  /** Module Instantiation Phase - Creating all service, client, repository, and container instances */
  INSTANTIATION = 'instantiation',

  /** Dependency Configuration Phase - Setting up IoC container and resolving dependencies */
  CONFIGURATION = 'configuration',

  /** Critical Services Initialization - Core platform services startup in strict order */
  CRITICAL_SERVICES_INIT = 'critical_services_init',

  /** Behavior Configuration Phase - Applying service behaviors and runtime configurations */
  RUNTIME_CONFIG_BY_BEHAVIOR = 'runtime_config_by_behavior',

  /** Service Initialization Phase - Parallel initialization of remaining services */
  INITIALIZATION = 'initialization',

  /** API Integration Phase - Establishing Cross-Service communication */
  INTEGRATION = 'integration',

  /** Legacy System Integration Phase - Deprecated services for backward compatibility */
  NON_CANONICAL_INITIALIZATION = 'non_canonical_initialization',

  /** Post-Integration Finalization Phase - Background tasks, monitoring, and optimization */
  POST_INTEGRATION = 'post_integration',

  /** Initialization completed successfully - System fully operational */
  SUCCESS = 'success',

  /** Initialization failed with error - Recovery procedures may be attempted */
  ERROR = 'error'
}

/**
 * Numeric representation of bootstrap states for ordering and progress tracking
 * Each state has a sequential number representing its position in the initialization flow
 */
export const BootstrapStateNumbers = {
  [BootstrapState.IDLE]: 0,
  [BootstrapState.CONFIGURATION_LOADING]: 1,
  [BootstrapState.INSTANTIATION]: 2,
  [BootstrapState.CONFIGURATION]: 3,
  [BootstrapState.CRITICAL_SERVICES_INIT]: 4,
  [BootstrapState.RUNTIME_CONFIG_BY_BEHAVIOR]: 5,
  [BootstrapState.INITIALIZATION]: 6,
  [BootstrapState.INTEGRATION]: 7,
  [BootstrapState.NON_CANONICAL_INITIALIZATION]: 8,
  [BootstrapState.POST_INTEGRATION]: 9,
  [BootstrapState.SUCCESS]: 10,
  [BootstrapState.ERROR]: -1 // Error state is special, not part of normal progression
} as const;

/**
 * Get the numeric representation of a bootstrap state
 * @param state - The bootstrap state
 * @returns The numeric representation of the state
 */
export function getBootstrapStateNumber(state: BootstrapState): number {
  return BootstrapStateNumbers[state];
}

/**
 * Get the progress percentage based on the current state
 * @param state - The bootstrap state
 * @returns Progress percentage (0-100), or -1 for error state
 */
export function getBootstrapProgress(state: BootstrapState): number {
  const stateNumber = getBootstrapStateNumber(state);

  if (stateNumber === -1) {
    return -1; // Error state
  }

  const maxProgress = 10; // SUCCESS state number
  return Math.round((stateNumber / maxProgress) * 100);
}

/**
 * Bootstrap state messages for user feedback and debugging
 * Provides human-readable descriptions of each initialization phase
 */
export const BootstrapStateMessages = {
  [BootstrapState.IDLE]: 'System ready to initialize',
  [BootstrapState.CONFIGURATION_LOADING]:
    'Loading and enhancing manifest configuration...',
  [BootstrapState.INSTANTIATION]:
    'Creating service, client, repository, and container instances...',
  [BootstrapState.CONFIGURATION]:
    'Configuring dependency injection and resolving service dependencies...',
  [BootstrapState.CRITICAL_SERVICES_INIT]:
    'Initializing core platform services (localization, navigation, session)...',
  [BootstrapState.RUNTIME_CONFIG_BY_BEHAVIOR]:
    'Applying service behaviors and runtime configurations...',
  [BootstrapState.INITIALIZATION]:
    'Initializing remaining services in parallel...',
  [BootstrapState.INTEGRATION]:
    'Establishing cross-service communication and integration...',
  [BootstrapState.NON_CANONICAL_INITIALIZATION]:
    'Integrating legacy services for backward compatibility...',
  [BootstrapState.POST_INTEGRATION]:
    'Setting up monitoring, background tasks, and finalizing system...',
  [BootstrapState.SUCCESS]: 'System initialization completed successfully',
  [BootstrapState.ERROR]:
    'System initialization failed - recovery may be attempted'
} as const;
